"use client";

import { useEffect, useRef } from "react";
import { useI18n } from "@/i18n/context";
import { useConsent } from "./ConsentContext";
import "./consent.css";

export function ConsentBanner() {
  const { t } = useI18n();
  const { bannerOpen, status, accept, decline, closeBanner } = useConsent();
  const boxRef = useRef<HTMLDivElement>(null);

  // The first prompt has to be answered — pressing Escape is not consent, and
  // silently dismissing it would leave us measuring nothing while pretending to
  // have asked. Once a choice exists, reopening it is just a settings panel.
  const dismissible = status !== "unset";

  useEffect(() => {
    if (!bannerOpen) return;
    // Focus the panel, not a button: putting focus on "Accept" would nudge the
    // keyboard path toward acceptance.
    boxRef.current?.focus();
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape" && dismissible) closeBanner();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [bannerOpen, dismissible, closeBanner]);

  if (!bannerOpen) return null;

  return (
    <div className="wz-consent">
      <div
        className="wz-consent-inner"
        role="dialog"
        aria-labelledby="wz-consent-title"
        aria-describedby="wz-consent-text"
        tabIndex={-1}
        ref={boxRef}
      >
        <div className="wz-consent-copy">
          <p className="wz-consent-title" id="wz-consent-title">
            {t("Audience measurement")}
          </p>
          {/* First layer states the purpose and who is asking; the tool, the
              cookie names, their lifetime and the transfers outside the EU are
              one click away in the privacy policy, which is what the CNIL's
              two-layer model asks for. "Aggregate" and not "anonymous": GA
              identifies a browser, so calling it anonymous would be false. */}
          <p className="wz-consent-text" id="wz-consent-text">
            {t(
              "Aggregate visit statistics, so we know which pages get read. Nothing is loaded before you agree.",
            )}
          </p>
          <p className="wz-consent-links">
            <a href="/legal/privacy#p-10">{t("Learn more")}</a>
            {dismissible && (
              <span className="wz-consent-current">
                {status === "granted"
                  ? t("Current choice: accepted")
                  : t("Current choice: refused")}
              </span>
            )}
          </p>
        </div>

        {/* Refuse and Accept share one class on purpose: refusing has to be as
            easy, and as visible, as accepting. Don't make one of them primary. */}
        <div className="wz-consent-actions">
          <button type="button" className="wz-consent-btn" onClick={decline}>
            {t("Refuse")}
          </button>
          <button type="button" className="wz-consent-btn" onClick={accept}>
            {t("Accept")}
          </button>
        </div>

        {dismissible && (
          <button
            type="button"
            className="wz-consent-close"
            onClick={closeBanner}
            aria-label={t("Close")}
          >
            ×
          </button>
        )}
      </div>
    </div>
  );
}
